// src/components/typing/StatsBar.js
import React from "react";

// ─── Helpers ──────────────────────────────────────────────────────
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const getTimeColor = (timeLeft, timeLimit) => {
  const pct = timeLeft / timeLimit;
  if (pct <= 0.1 || timeLeft <= 10) return "#ef4444";
  if (pct <= 0.25 || timeLeft <= 30) return "#f59e0b";
  return "#10b981";
};

const getAccuracyColor = (accuracy) => {
  if (accuracy >= 95) return "#10b981";
  if (accuracy >= 80) return "#f59e0b";
  return "#ef4444";
};

// ─── Stat Card ────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, unit, valueColor, pulse, hidden }) => {
  if (hidden) return null;
  return (
    <div className={`stat-card ${pulse ? "pulse" : ""}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div
          className="stat-value"
          style={{ color: valueColor || "var(--accent-purple)" }}
        >
          {value}
          {unit && <span className="stat-unit">{unit}</span>}
        </div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
};

// ─── Big Timer (Test Mode live) ───────────────────────────────────
const BigTimer = ({ timeLeft, timeLimit }) => {
  const color    = getTimeColor(timeLeft, timeLimit);
  const isUrgent = timeLeft <= 10;
  const pct      = Math.max(0, (timeLeft / timeLimit) * 100);

  return (
    <div className={`big-timer-wrapper ${isUrgent ? "urgent" : ""}`}>
      <div className="big-timer-label">⏱️ Time Remaining</div>
      <div className="big-timer-value" style={{ color }}>
        {formatTime(timeLeft)}
      </div>
      <div className="big-timer-track">
        <div
          className="big-timer-fill"
          style={{
            width: `${pct}%`,
            background: color,
            transition: "width 1s linear, background 0.5s ease",
          }}
        />
      </div>
      {isUrgent && timeLeft > 0 && (
        <div className="big-timer-urgent-text">⚠️ Hurry up!</div>
      )}
    </div>
  );
};

// ─── StatsBar ─────────────────────────────────────────────────────
/**
 * Props:
 *   stats      — live or final stats object
 *   mode       — "practice" | "test"
 *   timeLimit  — number (seconds)
 *   isFinished — boolean: when true, renders the FINAL result card
 *   finalStats — the full finalStats object (only used when isFinished)
 */
const StatsBar = ({
  stats,
  mode = "practice",
  timeLimit,
  isFinished = false,
  finalStats = null,
}) => {
  const { wpm, cpm, accuracy, errors, keystrokes, timeLeft, progress } = stats;

  const isTest        = mode === "test";
  const timeColor     = getTimeColor(timeLeft, timeLimit || 60);
  const accuracyColor = getAccuracyColor(accuracy);
  const isUrgent      = timeLeft <= 10;

  // ── FINISHED STATE: single unified result card ─────────────────
  if (isFinished && finalStats) {
    const fs             = finalStats;
    const fAccuracyColor = getAccuracyColor(fs.accuracy);
    const timeUsedStr    = formatTime(fs.timeUsed);

    return (
      <div className={`stats-bar-wrapper finished-stats ${isTest ? "test-mode-stats" : ""}`}>

        {/* ── Result Cards Row ── */}
        <div className="stats-cards-row">

          <StatCard
            icon="⚡"
            label="WPM"
            value={fs.wpm}
            valueColor="var(--accent-purple)"
          />

          <StatCard
            icon="🔤"
            label="CPM"
            value={fs.cpm}
            valueColor="#6366f1"
          />

          <StatCard
            icon="🎯"
            label="Accuracy"
            value={fs.accuracy}
            unit="%"
            valueColor={fAccuracyColor}
          />

          <StatCard
            icon="❌"
            label="Wrong Words"
            value={fs.wrongWords}
            valueColor={fs.wrongWords === 0 ? "#10b981" : "#ef4444"}
          />

          <StatCard
            icon="⌨️"
            label="Keystrokes"
            value={fs.keystrokes ?? 0}
            valueColor="#06b6d4"
          />

          <StatCard
            icon="⏱️"
            label="Time Used"
            value={timeUsedStr}
            valueColor="#8b5cf6"
          />

          <StatCard
            icon="✅"
            label="Correct Chars"
            value={`${fs.correctChars}/${fs.totalChars}`}
            valueColor="#10b981"
          />

          <StatCard
            icon="📋"
            label="Completion"
            value={fs.completedFully ? "Full" : "Partial"}
            valueColor={fs.completedFully ? "#10b981" : "#f59e0b"}
          />

        </div>

        {/* ── Word Score Breakdown ── */}
        {fs.totalWordCount > 0 && (
          <div className="stats-word-breakdown">

            <div className="stats-word-breakdown-title">📊 Word Breakdown</div>

            <div className="stats-word-breakdown-row">

              <div className="swb-item correct">
                <span className="swb-icon">✅</span>
                <span className="swb-value">{fs.rightWords}</span>
                <span className="swb-label">Right</span>
              </div>

              <div className="swb-item wrong">
                <span className="swb-icon">❌</span>
                <span className="swb-value">{fs.wrongWords}</span>
                <span className="swb-label">Wrong</span>
              </div>

              <div className="swb-item missed">
                <span className="swb-icon">🟡</span>
                <span className="swb-value">{fs.missedWords}</span>
                <span className="swb-label">Missed</span>
              </div>

              <div className="swb-item pending">
                <span className="swb-icon">⏳</span>
                <span className="swb-value">{fs.pendingWords}</span>
                <span className="swb-label">Pending</span>
              </div>

              {fs.extraWords > 0 && (
                <div className="swb-item extra">
                  <span className="swb-icon">➕</span>
                  <span className="swb-value">{fs.extraWords}</span>
                  <span className="swb-label">Extra</span>
                </div>
              )}

              <div className="swb-item total">
                <span className="swb-icon">📋</span>
                <span className="swb-value">
                  {fs.typedWordCount}/{fs.totalWordCount}
                </span>
                <span className="swb-label">Typed/Total</span>
              </div>

            </div>

            {/* Accuracy formula */}
            <div className="stats-accuracy-formula">
              Accuracy = {fs.rightWords} ÷ {fs.typedWordCount} × 100
              {" = "}
              <strong style={{ color: fAccuracyColor }}>{fs.accuracy}%</strong>
            </div>

          </div>
        )}

        {/* ── Progress Bar ── */}
        <div className="stats-progress-section">
          <div className="stats-progress-header">
            <span className="stats-progress-label">Progress</span>
            <span className="stats-progress-percent">{progress}%</span>
          </div>
          <div className="stats-progress-track">
            <div
              className="stats-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

      </div>
    );
  }

  // ── LIVE STATE ─────────────────────────────────────────────────
  return (
    <div className={`stats-bar-wrapper ${isTest ? "test-mode-stats" : ""}`}>

      {/* Big Timer — test mode only */}
      {isTest && (
        <BigTimer timeLeft={timeLeft} timeLimit={timeLimit || 60} />
      )}

      {/* Stat Cards */}
      <div className="stats-cards-row">

        <StatCard
          icon="⚡"
          label="WPM"
          value={wpm}
          valueColor="var(--accent-purple)"
        />

        <StatCard
          icon="🔤"
          label="CPM"
          value={cpm}
          valueColor="#6366f1"
        />

        <StatCard
          icon="🎯"
          label="Accuracy"
          value={accuracy}
          unit="%"
          valueColor={accuracyColor}
          hidden={isTest}
        />

        <StatCard
          icon="❌"
          label="Errors"
          value={errors}
          valueColor={errors === 0 ? "#10b981" : "#ef4444"}
          hidden={isTest}
        />

        <StatCard
          icon="⌨️"
          label="Keystrokes"
          value={keystrokes}
          valueColor="#06b6d4"
        />

        {!isTest && (
          <StatCard
            icon="⏱️"
            label="Time Left"
            value={formatTime(timeLeft)}
            valueColor={timeColor}
            pulse={isUrgent}
          />
        )}

      </div>

      {/* Progress Bar */}
      <div className="stats-progress-section">
        <div className="stats-progress-header">
          <span className="stats-progress-label">Progress</span>
          <span className="stats-progress-percent">{progress}%</span>
        </div>
        <div className="stats-progress-track">
          <div
            className="stats-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Urgency warning — practice only */}
      {!isTest && isUrgent && timeLeft > 0 && (
        <div className="urgency-warning">
          ⚠️ Hurry up! Only <strong>{timeLeft}s</strong> left!
        </div>
      )}

    </div>
  );
};

export default StatsBar;